import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DONATE_URL } from "@/lib/links";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { GraduationCap, MapPin, Mail, BookOpen, ArrowRight, Flag, UserPlus, X, Loader2, Send } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { api, API } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1765812515298-f299f9e29b68?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.08 } }),
};

export default function Schools() {
  const [schools, setSchools] = useState([]);
  const [enrollSchool, setEnrollSchool] = useState(null);
  const [form, setForm] = useState({ parent_name: "", email: "", phone: "", student_grade: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/schools").then(({ data }) => setSchools(data)).catch(() => {});
  }, []);

  const openEnroll = (s) => {
    setForm({ parent_name: "", email: "", phone: "", student_grade: "", message: "" });
    setEnrollSchool(s);
  };

  const submitEnroll = async (e) => {
    e.preventDefault();
    if (!form.parent_name.trim() || !form.email.trim()) { toast.error("Please enter your name and email."); return; }
    setSubmitting(true);
    try {
      await api.post(`/schools/${enrollSchool.id}/enroll`, form);
      toast.success("Thank you! We'll be in touch about " + enrollSchool.name + ".");
      setEnrollSchool(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit. Please try again.");
    } finally { setSubmitting(false); }
  };

  const LEVELS = ["Elementary School", "Middle School", "High School"];
  const groupedByLevel = (() => {
    const groups = LEVELS.map((lvl) => [lvl, schools.filter((s) => s.level === lvl)]).filter(([, arr]) => arr.length);
    const others = schools.filter((s) => !LEVELS.includes(s.level));
    if (others.length) groups.push(["Other Campuses", others]);
    return groups;
  })();

  return (
    <div>
      <Navbar />

      {/* HERO */}
      <PageHero
        image={HERO}
        imageAlt="Classroom with American flag"
        kicker="Educating The Next Generation"
        title={<>United Patriot <span className="text-[#D90429]">Schools</span></>}
        subtitle="A planned network of schools committed to teaching real American History — the way it was taught between 1940 and 2000. No revisionism. Just the truth, the documents, and the heroes that built and defended this nation."
      />

      {/* MISSION */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="flex items-center gap-3 mb-8">
            <Flag className="w-8 h-8 text-[#D90429]" />
            <h2 className="font-display text-4xl sm:text-5xl uppercase leading-[0.95]">Our Mission</h2>
          </div>
          <div className="space-y-5 text-slate-600 text-lg leading-relaxed">
            <p>
              For decades, American classrooms taught our children the full story of this nation —
              the courage of the founding, the meaning of the Constitution and the Bill of Rights,
              and the sacrifices of every generation that kept us free. Somewhere along the way, that
              education was watered down and rewritten.
            </p>
            <p>
              <strong className="text-[#0A1128]">United Patriot Schools</strong> exists to restore it.
              Our planned campuses will teach real American History using the proven, fact-based
              curriculum of the mid-20th century — the standards in place from roughly 1940 to 2000 —
              grounded in primary-source documents, honest scholarship, and a genuine love of country.
            </p>
            <p>
              We are building schools where students learn to read, reason, and lead — where character,
              discipline, and citizenship come first, and where every young patriot understands the
              blessings and the responsibilities of liberty.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-6 mt-14">
            {[
              [BookOpen, "Truthful Curriculum", "History taught straight from the founding documents and primary sources."],
              [Flag, "Love of Country", "Civics, patriotism, and the duties of free citizens at the core."],
              [GraduationCap, "Real Standards", "The rigorous academic standards that built generations of leaders."],
            ].map(([Icon, title, text], i) => (
              <motion.div key={title} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i} className="border-l-2 border-[#D90429] pl-5">
                <Icon className="w-7 h-7 text-[#D90429] mb-3" />
                <h3 className="font-display text-lg uppercase leading-tight">{title}</h3>
                <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PLANNED SCHOOLS */}
      <section className="py-24 bg-muted">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex items-center gap-3 mb-4">
            <GraduationCap className="w-8 h-8 text-[#D90429]" />
            <h2 className="font-display text-4xl sm:text-5xl uppercase leading-[0.95]">Planned Schools</h2>
          </div>
          <p className="text-slate-500 mb-12 max-w-2xl">Campuses in development across the nation, organized by level. Locations and timelines are being finalized — join us to help bring them to life.</p>

          {schools.length === 0 ? (
            <p className="text-slate-400 text-center py-12">New campuses are being announced soon — check back shortly.</p>
          ) : (
            groupedByLevel.map(([level, list]) => (
              <div key={level} className="mb-16 last:mb-0" data-testid={`school-level-group`}>
                <div className="flex items-center gap-3 mb-6">
                  <span className="h-[2px] w-8 bg-[#D90429]" />
                  <h3 className="font-display text-2xl sm:text-3xl uppercase text-[#0A1128]">{level}</h3>
                  <span className="text-xs uppercase tracking-[0.2em] text-slate-400">{list.length} campus{list.length === 1 ? "" : "es"}</span>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {list.map((s, i) => (
                    <motion.div
                      key={s.id}
                      initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
                      data-testid="school-card"
                      className="bg-white border border-slate-200 hover:border-[#D90429] transition-colors duration-200 overflow-hidden flex flex-col group"
                    >
                      <div className="h-48 bg-[#0A1128] overflow-hidden relative">
                        {s.image_file_id ? (
                          <img src={`${API}/public/files/${s.image_file_id}`} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><GraduationCap className="w-12 h-12 text-white/25" /></div>
                        )}
                        {s.status && <span className="absolute top-3 left-3 bg-[#D90429] text-white text-[10px] uppercase tracking-[0.15em] font-bold px-3 py-1.5">{s.status}</span>}
                      </div>
                      <div className="p-6 flex-1 flex flex-col">
                        <h4 className="font-display text-2xl uppercase leading-tight text-[#0A1128]">{s.name}</h4>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs uppercase tracking-[0.12em] text-slate-500">
                          {s.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[#D90429]" /> {s.location}</span>}
                          {s.grade_levels && <span className="inline-flex items-center gap-1"><BookOpen className="w-3.5 h-3.5 text-[#D90429]" /> {s.grade_levels}</span>}
                        </div>
                        {s.description && <p className="text-sm text-slate-600 mt-4 leading-relaxed flex-1">{s.description}</p>}
                        {s.contact_email && (
                          <a href={`mailto:${s.contact_email}`} data-testid={`school-contact-${s.id}`} className="inline-flex items-center gap-1.5 mt-4 text-xs uppercase font-bold text-[#0A1128] hover:text-[#D90429] transition-colors duration-200">
                            <Mail className="w-3.5 h-3.5" /> {s.contact_email}
                          </a>
                        )}
                        <button
                          data-testid={`school-enroll-${s.id}`}
                          onClick={() => openEnroll(s)}
                          className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#0A1128] text-white px-5 py-3 uppercase tracking-[0.15em] text-xs font-bold hover:bg-[#D90429] transition-colors duration-200 active:scale-95"
                        >
                          <UserPlus className="w-4 h-4" /> Register Your Interest
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A1128] text-white py-20 grain stars relative">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center relative z-10">
          <GraduationCap className="w-10 h-10 text-[#D90429] mx-auto mb-6" />
          <h2 className="font-display text-4xl sm:text-5xl uppercase leading-[0.95]">Help Us Build These Schools</h2>
          <p className="text-white/70 mt-5 max-w-xl mx-auto">Every patriot, every dollar, brings a classroom closer to reality.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link to="/join" data-testid="schools-join-button" className="bg-white text-[#0A1128] px-8 py-4 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#F4A261] transition-colors duration-200 active:scale-95">Join Our Newsletter</Link>
            <a href={DONATE_URL} target="_blank" rel="noopener noreferrer" data-testid="schools-donate-button" className="inline-flex items-center justify-center gap-2 bg-[#D90429] text-white px-8 py-4 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200 active:scale-95">Donate <ArrowRight className="w-4 h-4" /></a>
          </div>
        </div>
      </section>

      {/* ENROLLMENT MODAL */}
      <AnimatePresence>
        {enrollSchool && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="enroll-modal"
            className="fixed inset-0 z-[100] bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
            onClick={() => !submitting && setEnrollSchool(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white w-full max-w-lg my-4" onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-[#0A1128] text-white p-6 flex items-start justify-between">
                <div>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#F4A261] font-bold">{enrollSchool.level}</span>
                  <h3 className="font-display text-2xl uppercase mt-1 leading-tight">Register Your Interest</h3>
                  <p className="text-white/60 text-sm mt-1">{enrollSchool.name}</p>
                </div>
                <button data-testid="enroll-close" onClick={() => !submitting && setEnrollSchool(null)} className="text-white/60 hover:text-white"><X className="w-6 h-6" /></button>
              </div>
              <form onSubmit={submitEnroll} className="p-6 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-1.5">Your Name *</label>
                    <input data-testid="enroll-name" required value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} className="w-full border border-slate-300 px-4 py-2.5 focus:outline-none focus:border-[#D90429]" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-1.5">Email *</label>
                    <input data-testid="enroll-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-slate-300 px-4 py-2.5 focus:outline-none focus:border-[#D90429]" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-1.5">Phone</label>
                    <input data-testid="enroll-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-slate-300 px-4 py-2.5 focus:outline-none focus:border-[#D90429]" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-1.5">Student Grade</label>
                    <input data-testid="enroll-grade" placeholder="e.g. 3rd grade" value={form.student_grade} onChange={(e) => setForm({ ...form, student_grade: e.target.value })} className="w-full border border-slate-300 px-4 py-2.5 focus:outline-none focus:border-[#D90429]" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-1.5">Message (optional)</label>
                  <textarea data-testid="enroll-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full border border-slate-300 px-4 py-2.5 resize-none focus:outline-none focus:border-[#D90429]" placeholder="Tell us about your family and any questions you have." />
                </div>
                <button data-testid="enroll-submit" type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 bg-[#D90429] text-white px-6 py-3.5 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200 disabled:opacity-60">
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <><Send className="w-4 h-4" /> Submit Interest</>}
                </button>
                <p className="text-xs text-slate-400 text-center">We'll reach out with updates as this campus develops. No spam, ever.</p>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
